// eslint-disable-next-line @typescript-eslint/no-var-requires
const tokens = require('./tokens.json');
// ---------------------------------------------------------------------------
// Primitive color scales
// ---------------------------------------------------------------------------
// Sourced from tokens.json where stops exist (50, 100, 300, 500, 700, 900).
// Intermediate stops (200, 400, 600, 800) are kept as manual fills to give
// component overrides the full 50–900 range MUI consumers expect.
// ---------------------------------------------------------------------------
const p = tokens.Primitives.color;
export const brand = {
    50: p.blue['50'].value,
    100: p.blue['100'].value,
    200: '#93cdf5',
    300: p.blue['300'].value,
    400: '#1a8fd9',
    500: p.blue['500'].value,
    600: '#0c6aab',
    700: p.blue['700'].value,
    800: '#0a5690',
    900: p.blue['900'].value,
};
export const gray = {
    50: p.neutral['50'].value,
    100: p.neutral['100'].value,
    200: '#e0e0e0',
    300: p.neutral['300'].value,
    400: '#b3b3b3',
    500: p.neutral['500'].value,
    600: '#858585',
    700: p.neutral['700'].value,
    800: '#555555',
    900: p.neutral['900'].value,
    1000: p.neutral['1000'].value,
};
export const green = {
    50: p.green['50'].value,
    100: p.green['100'].value,
    200: '#c4e1c1',
    300: p.green['300'].value,
    400: '#7dbf77',
    500: p.green['500'].value,
    600: '#519a4a',
    700: p.green['700'].value,
    800: '#396e34',
    900: p.green['900'].value,
};
export const orange = {
    50: p.orange['50'].value,
    100: p.orange['100'].value,
    200: '#fad9a0',
    300: p.orange['300'].value,
    400: '#eba83a',
    500: p.orange['500'].value,
    600: '#bc7d0a',
    700: p.orange['700'].value,
    800: '#8f5d07',
    900: p.orange['900'].value,
};
export const red = {
    50: p.red['50'].value,
    100: p.red['100'].value,
    200: '#f1c0c0',
    300: p.red['300'].value,
    400: '#de5d5d',
    500: p.red['500'].value,
    600: '#d23434',
    700: p.red['700'].value,
    800: '#b12424',
    900: p.red['900'].value,
};
export const yellow = {
    50: p.yellow['50'].value,
    100: p.yellow['100'].value,
    200: '#fcea9b',
    300: p.yellow['300'].value,
    400: '#f8d63b',
    500: p.yellow['500'].value,
    600: '#dab205',
    700: p.yellow['700'].value,
    800: '#947804',
    900: p.yellow['900'].value,
};
export const purple = {
    50: p.purple['50'].value,
    100: p.purple['100'].value,
    200: '#d5c3ed',
    300: p.purple['300'].value,
    400: '#9c76d8',
    500: p.purple['500'].value,
    600: '#7545c5',
    700: p.purple['700'].value,
    800: '#523099',
    900: p.purple['900'].value,
};
// ---------------------------------------------------------------------------
// Semantic color tokens (from tokens.json "Colors" layer)
// Use these in components for intent-driven styling rather than raw scales.
// ---------------------------------------------------------------------------
export const semantic = {
    bg: {
        primary: p.neutral['0'].value,
        secondary: p.neutral['50'].value,
        tertiary: p.neutral['100'].value,
        disabled: p.neutral['50'].value,
        overlay: tokens.Colors.bg.overlay.value,
        overlayDark: tokens.Colors.bg['overlay-dark'].value,
        brand: p.blue['50'].value,
        success: p.green['50'].value,
        caution: p.yellow['50'].value,
        warning: p.orange['50'].value,
        critical: p.red['50'].value,
        admin: p.purple['50'].value,
        fillBrand: p.blue['100'].value,
        fillSuccess: p.green['100'].value,
        fillCaution: p.yellow['100'].value,
        fillWarning: p.orange['100'].value,
        fillCritical: p.red['100'].value,
        fillDisabled: p.neutral['300'].value,
        fillAdmin: p.purple['100'].value,
        solidBrand: p.blue['500'].value,
        solidCritical: p.red['500'].value,
        solidSuccess: p.green['500'].value,
        solidAdmin: p.purple['500'].value,
    },
    fg: {
        primary: p.neutral['900'].value,
        secondary: p.neutral['700'].value,
        inactive: p.neutral['500'].value,
        onSolid: p.neutral['0'].value,
        brand: p.blue['500'].value,
        success: p.green['500'].value,
        caution: p.yellow['500'].value,
        warning: p.orange['500'].value,
        critical: p.red['500'].value,
        admin: p.purple['500'].value,
    },
    text: {
        primary: p.neutral['900'].value,
        secondary: p.neutral['700'].value,
        disabled: p.neutral['500'].value,
        onSolid: p.neutral['0'].value,
        link: p.blue['500'].value,
        brand: p.blue['900'].value,
        success: p.green['900'].value,
        caution: p.yellow['900'].value,
        warning: p.orange['900'].value,
        critical: p.red['900'].value,
        admin: p.purple['900'].value,
    },
    border: {
        default: p.neutral['300'].value,
        defaultLight: p.neutral['100'].value,
        defaultHover: p.neutral['500'].value,
        brand: p.blue['300'].value,
        brandLight: p.blue['100'].value,
        success: p.green['300'].value,
        successLight: p.green['100'].value,
        caution: p.yellow['300'].value,
        cautionLight: p.yellow['100'].value,
        warning: p.orange['300'].value,
        warningLight: p.orange['100'].value,
        critical: p.red['300'].value,
        criticalLight: p.red['100'].value,
        admin: p.purple['300'].value,
        adminLight: p.purple['100'].value,
    },
};
// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------
// tokens.json carries three layout sets -- Layout (default), LayoutCompact and
// LayoutLoose. Only the default set is wired into the theme; the other two exist
// in Figma as density variants and have no consumer here yet.
//
// Values are px numbers rather than strings so the theme can hand them to
// Mantine's rem() helper, which emits `calc(Xrem * var(--mantine-scale))`. That
// form matters: a raw px string would drop the scale multiplier and stop
// honouring the user's browser font size.
// ---------------------------------------------------------------------------
const px = (v) => parseInt(v, 10);
const layout = tokens.Layout;
/** Layout/radius. xs–lg happen to equal Mantine 9's own defaults exactly. */
export const radiusScale = {
    xs: px(layout.radius.xs.value), // 2
    sm: px(layout.radius.sm.value), // 4
    md: px(layout.radius.md.value), // 8
    lg: px(layout.radius.lg.value), // 16
};
/** Layout/radius/full -- a pill, not a corner size. Deliberately NOT theme.radius.xl. */
export const radiusFull = px(layout.radius.full.value); // 100
/**
 * Layout/spacing, as proposed in tokens.json -- NOT what theme.spacing actually
 * ships (see Styles/mantineTheme.ts's `spacing`, which is Mantine's own
 * defaults, declared deliberately). Differs from Mantine's defaults at xs
 * (10->4), sm (12->8) and lg (20->24). Adopting this scale was considered and
 * declined 2026-09-02 -- it was never wired into the theme, so it never
 * shipped, and every existing gap="xs"/p="sm" call site across the app already
 * reads as Mantine's numbers. Kept here for the UI kit's own comparison, not
 * as a source anything should import for real layout.
 */
export const spacingScale = {
    xs: px(layout.spacing.xs.value), // 4
    sm: px(layout.spacing.sm.value), // 8
    md: px(layout.spacing.md.value), // 16
    lg: px(layout.spacing.lg.value), // 24
    xl: px(layout.spacing.xl.value), // 32
};
